import {
  Component,
  HostListener,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { UserRole } from '#enums';
import { AuthService, OpenCaseBarService } from '#services';
import { SidebarNavBtnComponent } from './sidebar-nav-btn/sidebar-nav-btn';
import { SidebarNavSectionComponent } from './sidebar-nav-section/sidebar-nav-section';

interface NavItem {
  labelKey: string;
  icon: string;
  path?: string;
  // Roles allowed to see this item. Omit to show to all authenticated users.
  roles?: UserRole[];
}

@Component({
  selector: 'app-sidebar',
  imports: [SidebarNavBtnComponent, SidebarNavSectionComponent],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  host: {
    '[style.width]': 'isMobile() ? null : sidebarWidth',
    '[style.min-width]': 'isMobile() ? null : sidebarWidth',
  },
})
export class Sidebar {
  isOpen = input(true);
  isMobile = input(false);
  toggleSidebar = output<void>();

  private _router = inject(Router);
  private _openCaseBarService = inject(OpenCaseBarService);
  private _authService = inject(AuthService);

  private _userDetails = toSignal(this._authService.userDetails$, {
    initialValue: null,
  });

  private _majorExpanded = signal(true);
  private _minorExpanded = signal(false);
  majorExpanded = this._majorExpanded.asReadonly();
  minorExpanded = this._minorExpanded.asReadonly();

  private _resizing = false;
  private _resizeStartX = 0;
  private _resizeStartWidth = 0;
  private _sidebarWidthPx = signal(220);

  readonly minWidth = 160;
  readonly maxWidth = 400;

  // Role-menu matrix — see docs/modules/M001-user-management for the authoritative
  // role-to-nav-item matrix. Items with no `roles` are visible to all authenticated users.
  private readonly _quickButtons: NavItem[] = [
    {
      labelKey: 'nav.jobManagement',
      icon: 'pi-table',
      path: '/job-management',
    },
    {
      labelKey: 'nav.allOpenCases',
      icon: 'pi-folder-open',
    },
    {
      labelKey: 'nav.options',
      icon: 'pi-cog',
      path: '/options',
      roles: [
        UserRole.Admin,
        UserRole.ALS,
        UserRole.Land,
        UserRole.LandSupervisor,
        UserRole.PE,
        UserRole.PeSteward,
        UserRole.WLMEngineer,
        UserRole.WLMTeamLead,
        UserRole.TA,
        UserRole.PAWSR,
        UserRole.ChemicalAdvisor,
        UserRole.FieldOps,
        UserRole.FieldOpsSupervisors,
        UserRole.IOC,
        UserRole.WRO,
        UserRole.Scheduler,
        UserRole.PAEngineer,
        UserRole.FSM,
        UserRole.FSMSupervisor,
        UserRole.PACoordinator,
        UserRole.PASuperintendent,
        UserRole.WLMWSR,
        UserRole.Reader,
      ],
    },
  ];

  private readonly _majorJobItems: NavItem[] = [
    {
      labelKey: 'nav.submissions',
      icon: 'pi-file',
      path: '/submissions',
      roles: [
        UserRole.Admin,
        UserRole.ALS,
        UserRole.PE,
        UserRole.PeSteward,
        UserRole.WLMEngineer,
        UserRole.WLMTeamLead,
        UserRole.IOC,
        UserRole.PAEngineer,
        UserRole.PACoordinator,
        UserRole.Reader,
      ],
    },
    {
      labelKey: 'nav.land',
      icon: 'pi-map',
      path: '/land',
      roles: [
        UserRole.Admin,
        UserRole.Land,
        UserRole.LandSupervisor,
        UserRole.Reader,
      ],
    },
    {
      labelKey: 'nav.engineering',
      icon: 'pi-wrench',
      path: '/engineering',
      roles: [
        UserRole.Admin,
        UserRole.PE,
        UserRole.PeSteward,
        UserRole.WLMEngineer,
        UserRole.FSMSupervisor,
        UserRole.PACoordinator,
        UserRole.Reader,
      ],
    },
    {
      labelKey: 'nav.planning',
      icon: 'pi-list',
      path: '/planning',
      roles: [
        UserRole.Admin,
        UserRole.WLMEngineer,
        UserRole.WLMTeamLead,
        UserRole.TA,
        UserRole.PAWSR,
        UserRole.PAEngineer,
        UserRole.PACoordinator,
        UserRole.PASuperintendent,
        UserRole.WLMWSR,
        UserRole.Reader,
      ],
    },
    { labelKey: 'nav.scheduling', icon: 'pi-clock', path: '/scheduling' },
    {
      labelKey: 'nav.execution',
      icon: 'pi-play',
      path: '/execution',
      roles: [
        UserRole.Admin,
        UserRole.TA,
        UserRole.PAWSR,
        UserRole.PAEngineer,
        UserRole.PACoordinator,
        UserRole.PASuperintendent,
        UserRole.WLMWSR,
        UserRole.Reader,
      ],
    },
    {
      labelKey: 'nav.closeOut',
      icon: 'pi-times',
      path: '/close-out',
      roles: [
        UserRole.Admin,
        UserRole.ALS,
        UserRole.IOC,
        UserRole.PAEngineer,
        UserRole.Reader,
      ],
    },
  ];

  private readonly _minorJobItems: NavItem[] = [
    { labelKey: 'nav.nonRig', icon: 'pi-wrench', path: '/non-rig' },
    {
      labelKey: 'nav.casingPressure',
      icon: 'pi-gauge',
      path: '/casing-pressure',
    },
    {
      labelKey: 'nav.reclamation',
      icon: 'pi-sync',
      path: '/reclamation',
      roles: [
        UserRole.Admin,
        UserRole.FSM,
        UserRole.FSMSupervisor,
        UserRole.PACoordinator,
        UserRole.Reader,
      ],
    },
    {
      labelKey: 'nav.facilityDecommission',
      icon: 'pi-trash',
      path: '/facility-decommission',
      roles: [
        UserRole.Admin,
        UserRole.Land,
        UserRole.LandSupervisor,
        UserRole.PE,
        UserRole.FSM,
        UserRole.FSMSupervisor,
        UserRole.PACoordinator,
        UserRole.PASuperintendent,
        UserRole.Reader,
      ],
    },
  ];

  private _isVisible(item: NavItem): boolean {
    return !item.roles || this._authService.isAuthorized(item.roles);
  }

  quickButtons = computed(() => {
    this._userDetails();
    return this._quickButtons.filter((item) => this._isVisible(item));
  });

  majorJobItems = computed(() => {
    this._userDetails();
    return this._majorJobItems.filter((item) => this._isVisible(item));
  });

  minorJobItems = computed(() => {
    this._userDetails();
    return this._minorJobItems.filter((item) => this._isVisible(item));
  });


  isActive(path?: string): boolean {
    if (!path) {
      return false;
    }

    const currentPath = this.currentPath();

    return (
      currentPath === path ||
      currentPath.startsWith(`${path}/`) ||
      currentPath.startsWith(`${path}?`)
    );
  }

  currentPath(): string {
    const routerUrl = this._router.url;

    const usesActiveNavPath =
      routerUrl.startsWith('/rigless-review') ||
      routerUrl.startsWith('/submissions/rigless-details');

    if (!usesActiveNavPath) {
      return routerUrl;
    }

    const navigationState =
      (history.state as { activeNavPath?: string } | null) ?? null;

    return (
      navigationState?.activeNavPath ??
      this._openCaseBarService.activeCase()?.activeNavPath ??
      routerUrl
    );
  }

  navigate(path?: string): void {
    if (path) this._router.navigate([path]);
  }

  toggleSection(section: 'major' | 'minor'): void {
    if (section === 'major') {
      this._majorExpanded.update((v) => !v);
      if (this._majorExpanded()) {
        this._minorExpanded.set(false);
      }
    } else {
      this._minorExpanded.update((v) => !v);
      if (this._minorExpanded()) {
        this._majorExpanded.set(false);
      }
    }
  }

  startResize(event: MouseEvent): void {
    if (!this.isOpen() || this.isMobile()) return;
    this._resizing = true;
    this._resizeStartX = event.clientX;
    this._resizeStartWidth = this._sidebarWidthPx();
    event.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this._resizing) return;
    const delta = event.clientX - this._resizeStartX;
    this._sidebarWidthPx.set(
      Math.min(
        this.maxWidth,
        Math.max(this.minWidth, this._resizeStartWidth + delta),
      ),
    );
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this._resizing = false;
  }

  get sidebarWidth(): string {
    return this.isOpen() ? `${this._sidebarWidthPx()}px` : '56px';
  }
}
